import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import MessageEditor from "components/forms/MessageEditor";
import { useToast } from "components/toast";
import { getSiblingProposals, type SiblingProposal } from "api/Leads";
import { sendProposalToContact } from "api/Proposals";

/**
 * "Same proposal, this contact" on a lead's Proposals tab (2026-09-28).
 * Lists proposals already SENT to other contacts at this organization
 * (sibling leads). Sending one here makes the next version of that
 * proposal on THIS lead, with the issued prices unchanged, its own link
 * and its own opens. One proposal per organization; one version per person.
 */
export default function SiblingProposalOffers({
  leadId,
  contactName,
  contactEmail,
  onSent,
}: {
  leadId: string;
  contactName: string | null;
  contactEmail: string | null;
  onSent: () => void;
}) {
  const toast = useToast();
  const [items, setItems] = useState<SiblingProposal[]>([]);
  const [target, setTarget] = useState<SiblingProposal | null>(null);
  const [to, setTo] = useState(contactEmail ?? "");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => getSiblingProposals(leadId).then(setItems);
  useEffect(() => {
    load();
  }, [leadId]); // eslint-disable-line

  const offers = items.filter((p) => !p.alreadyHere);
  if (!offers.length) return null;

  const send = async (skipEmail = false) => {
    if (!target) return;
    if (!skipEmail && !to.trim()) return toast.add("An email address is needed.", "var(--theme-ui-colors-danger)");
    setBusy(true);
    const res: any = await sendProposalToContact(target.id, {
      leadId,
      sentTo: to.trim(),
      message: message.trim() || undefined,
      requirePassword: password,
      skipEmail,
    });
    setBusy(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    if (skipEmail && res?.shareUrl) {
      try {
        await navigator.clipboard.writeText(res.shareUrl);
      } catch {
        /* clipboard can be blocked; the link is on the proposal */
      }
    }
    toast.add(skipEmail ? "Link created and copied." : `Sent to ${to.trim()}.`, "var(--theme-ui-colors-success)");
    setTarget(null);
    load();
    onSent();
  };

  const input: React.CSSProperties = { padding: "7px 9px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box" };

  return (
    <Box sx={{ mb: 12, p: "12px 14px", background: "#EEF4FB", border: "1px solid #B9CFEA", borderRadius: 8, fontFamily: "Poppins", fontSize: 13 }}>
      <Box sx={{ fontWeight: 600, mb: "6px" }}>Already sent to others at this organization</Box>
      {offers.map((p) => (
        <Flex key={p.id} sx={{ alignItems: "center", gap: 10, py: "6px", borderTop: "1px solid #D5E3F3" }}>
          <Box sx={{ flex: 1 }}>
            <b>{p.name || p.prospectName}</b>
            {p.version > 1 ? <span style={{ color: themevals.colors.dkgrey }}> v{p.version}</span> : null}
            <Box sx={{ fontSize: 11, color: themevals.colors.dkgrey }}>
              {[
                `to ${p.contactName || p.sentTo || p.leadName}`,
                p.sentAt ? moment(p.sentAt).format("MMM D") : null,
                p.viewCount ? `${p.viewCount} open${p.viewCount === 1 ? "" : "s"}` : "not opened",
              ]
                .filter(Boolean)
                .join(" \u00b7 ")}
            </Box>
          </Box>
          <button
            type="button"
            onClick={() => { setTarget(p); setTo(contactEmail ?? ""); setMessage(""); }}
            style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0, fontFamily: "Poppins", fontSize: 12, fontWeight: 600 }}
          >
            Send to {contactName || "this contact"}
          </button>
        </Flex>
      ))}

      {target && (
        <BasicModal
          title={`Send ${target.name || target.prospectName} to ${contactName || "this contact"}`}
          size="md"
          cancel_click={() => setTarget(null)}
          cancel_text="Cancel"
          confirm_text={busy ? "Sending\u2026" : "Send"}
          confirm_click={() => send(false)}
          submitting={busy}
        >
          <Box sx={{ fontSize: 12, fontFamily: "Poppins", textAlign: "left", color: themevals.colors.dkgrey, mb: 12 }}>
            The same proposal, prices exactly as issued. It becomes the next version on this lead with its own link, so their opens and replies are tracked here.
          </Box>
          <Box sx={{ textAlign: "left", mb: 10 }}>
            <input style={input} type="email" placeholder="Email" value={to} onChange={(e) => setTo(e.target.value)} autoFocus />
          </Box>
          <Box sx={{ textAlign: "left", mb: 10 }}>
            <MessageEditor
              value={message}
              onChange={setMessage}
              placeholder="Message (optional). A View proposal button sits above it; the link repeats below."
              email={{
                shareUrl: null,
                password: null,
                hasPassword: password,
                includePassword: true,
                fromName: "The sender on the proposal",
                subject: `${target.prospectName} \u2014 preliminary pricing`,
                to: to || null,
              }}
            />
          </Box>
          <Box
            as="div"
            onClick={() => setPassword((v) => !v)}
            sx={{ display: "flex", gap: "8px", alignItems: "center", fontFamily: "Poppins", fontSize: 13, textAlign: "left", cursor: "pointer", color: "#1a2332" }}
          >
            <input type="checkbox" checked={password} readOnly style={{ width: 16, height: 16, margin: 0, flexShrink: 0 }} />
            <span>Protect the link with a password</span>
          </Box>
          <Box sx={{ textAlign: "left", mt: 10 }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => send(true)}
              style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0, fontFamily: "Poppins", fontSize: 12, fontWeight: 500 }}
            >
              Copy link instead of emailing
            </button>
          </Box>
        </BasicModal>
      )}
    </Box>
  );
}
